/**
 * Persistence utilities for Wealthica2 integration
 * 
 * This file contains utilities for managing account persistence operations.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import { ensureValidToken } from '@/services/AuthAwareApiService';
import { WealthicaAccount, WealthicaConnection } from '../types';
import { useAuthStore } from '@/stores/authStore';

// Debounce threshold in milliseconds to prevent duplicate operations (extended for long sessions)
export const DEBOUNCE_THRESHOLD = 5000;

// Event name for direct component communication
export const CONNECTION_STATE_CHANGE_EVENT = 'wealthica2:connection:state:change';

/**
 * Save accounts to the database
 * 
 * @param userId - User ID
 * @param connectionData - Connection data object
 * @param bearerToken - Bearer token for API authentication
 * @param accounts - Accounts to save
 */
export async function saveAccountsToDatabase(
  userId: string, 
  connectionData: WealthicaConnection,
  bearerToken: string,
  accounts: WealthicaAccount[]
): Promise<{success: boolean, error?: any, details?: any}> {
  try {
    logger.info('persistence-utils', 'Saving accounts', {
      accountCount: accounts?.length || 0,
      institutionId: connectionData.institutionId
    });

    // Prefer AuthStore to avoid network calls; fallback to cached session
    let authenticatedUserId = useAuthStore.getState().user?.id || null;
    if (!authenticatedUserId) {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user?.id) {
        logger.error('persistence-utils', 'Failed to get authenticated session', { error: sessionError });
        return { success: false, error: 'Authentication required' };
      }
      authenticatedUserId = session.user.id;
    }
    
    logger.debug('persistence-utils', 'User ID consistency check', {
      providedUserId: userId.substring(0, 8) + '...',
      authenticatedUserId: authenticatedUserId.substring(0, 8) + '...',
      match: userId === authenticatedUserId
    });

    // Local-mode save: do not require Wealthica bearer token on client
    const savePayload: any = {
      user_id: authenticatedUserId, // Use JWT user ID for consistency
      institution_id: connectionData.institutionId,
      provider_name: connectionData.providerName, // canonical provider ('wealthica' | 'demo')
      institution_identifier: connectionData.institutionIdentifier,
      accounts: accounts,
      operation_mode: 'local',
      force_local: !bearerToken
    };
    if (bearerToken) {
      savePayload.bearer_token = bearerToken;
    }

    logger.debug('persistence-utils', 'Save payload structure', {
      payloadKeys: Object.keys(savePayload),
      userIdPresent: !!userId,
      institutionIdPresent: !!connectionData.institutionId,
      bearerTokenPresent: !!bearerToken,
      operationMode: savePayload.operation_mode,
      forceLocal: savePayload.force_local,
      accountCount: accounts?.length || 0
    });

    await ensureValidToken('wealthica2-save-accounts');
    const { data, error } = await supabase.functions.invoke('wealthica2-save-accounts', {
      body: savePayload
    });

    if (error) {
      logger.error('persistence-utils', 'Failed to save accounts', { 
        error: error.message || error,
        statusCode: error.status || 'unknown',
        payloadKeys: Object.keys(savePayload),
        accountCount: accounts?.length || 0
      });
      return { success: false, error, details: { 
        statusCode: error.status,
        payloadStructure: Object.keys(savePayload)
      }};
    } else {
      const accSaved = (data?.data?.savedAccounts ?? data?.savedAccounts ?? data?.accounts_saved ?? 0);
      const integrityChecksCount = (data?.data?.integrity?.summary?.total_checks ?? data?.integrity?.summary?.total_checks ?? data?.integrity_check?.summary?.total_checks ?? 0);
      logger.info('persistence-utils', 'Successfully saved accounts', { 
        accountsSaved: accSaved,
        integrityChecks: integrityChecksCount,
        data 
      });
      
      // INTEGRITY: Enhanced response with integrity data
      const enhancedResponse = {
        success: true,
        details: {
          ...data,
          // Ensure integrity data is preserved for multi-store processing
          integrity_check: data?.integrity_check
        }
      };
      
      return enhancedResponse;
    }
  } catch (err) {
    logger.error('persistence-utils', 'Error saving accounts', { error: String(err) });
    return { success: false, error: err };
  }
}

/**
 * Save positions to the database
 * 
 * @param userId - User ID
 * @param connectionData - Connection data object
 * @param bearerToken - Bearer token for API authentication
 * @param positions - Positions to save
 */
export async function savePositionsToDatabase(
  userId: string, 
  connectionData: WealthicaConnection,
  bearerToken: string,
  positions: any[]
): Promise<{success: boolean, error?: any, details?: any}> {
  try {
    logger.info('persistence-utils', 'Saving positions', {
      positionCount: positions?.length || 0,
      institutionId: connectionData.institutionId,
    });

    // Prefer AuthStore to avoid network calls; fallback to cached session
    let authenticatedUserId = useAuthStore.getState().user?.id || null;
    if (!authenticatedUserId) {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user?.id) {
        logger.error('persistence-utils', 'Failed to get authenticated session for positions', { error: sessionError });
        return { success: false, error: 'Authentication required' };
      }
      authenticatedUserId = session.user.id;
    }

    const savePayload = {
      user_id: authenticatedUserId, // Use JWT user ID for consistency
      institution_id: connectionData.institutionId,
      provider_name: connectionData.providerName, // canonical provider ('wealthica' | 'demo')
      institution_identifier: connectionData.institutionIdentifier,
      positions: positions
    };

    logger.debug('persistence-utils', 'Position save payload structure', {
      payloadKeys: Object.keys(savePayload),
      userIdPresent: !!userId,
      institutionIdPresent: !!connectionData.institutionId,
      positionCount: positions?.length || 0
    });

    await ensureValidToken('wealthica2-save-positions');
    const { data, error } = await supabase.functions.invoke('wealthica2-save-positions', {
      body: savePayload
    });

    if (error) {
      logger.error('persistence-utils', 'Failed to save positions', { 
        error: error.message || error,
        statusCode: error.status || 'unknown',
        payloadKeys: Object.keys(savePayload),
        positionCount: positions?.length || 0
      });
      return { success: false, error, details: { 
        statusCode: error.status,
        payloadStructure: Object.keys(savePayload)
      }};
    } else {
      const posSaved = (data?.data?.count ?? data?.count ?? data?.positions_saved ?? 0);
      const integrityChecksCount2 = (data?.data?.integrity?.summary?.total_checks ?? data?.integrity?.summary?.total_checks ?? data?.integrity_check?.summary?.total_checks ?? 0);
      logger.info('persistence-utils', 'Successfully saved positions', { 
        positionsSaved: posSaved,
        integrityChecks: integrityChecksCount2,
        data 
      });
      
      // INTEGRITY: Enhanced response with integrity data
      const enhancedResponse = {
        success: true,
        details: {
          ...data,
          // Ensure integrity data is preserved for multi-store processing
          integrity_check: data?.integrity_check
        }
      };
      
      return enhancedResponse;
    }
  } catch (err) {
    logger.error('persistence-utils', 'Error saving positions', { error: String(err) });
    return { success: false, error: err };
  }
}

/**
 * Delete accounts and positions from the database
 * 
 * @param userId - User ID
 * @param connectionData - Connection data object
 * @param preserveConnection - Whether to preserve connection data
 */
export async function deleteAccountsAndPositionsFromDatabase(
  userId: string, 
  connectionData: WealthicaConnection,
  preserveConnectionOrOptions: boolean | { preserveConnection?: boolean; deleteMode?: 'stop_syncing' | 'full_delete' } = true
): Promise<{success: boolean, error?: any, details?: any}> {
  try {
    // Normalize options for backward compatibility
    const preserveConnection = typeof preserveConnectionOrOptions === 'boolean'
      ? preserveConnectionOrOptions
      : (preserveConnectionOrOptions?.preserveConnection ?? true);
    const explicitDeleteMode = typeof preserveConnectionOrOptions === 'object' && preserveConnectionOrOptions?.deleteMode
      ? preserveConnectionOrOptions.deleteMode
      : undefined;

    logger.info('persistence-utils', 'Deleting accounts and positions', {
      institutionId: connectionData.institutionId,
      preserveConnection,
      explicitDeleteMode
    });

    // Determine mode: prefer explicit deleteMode; default to 'stop_syncing' when preserving connection
    // This avoids unintended connection deletion for view-only OFF toggles (e.g. demo provider)
    const delete_mode: 'stop_syncing' | 'full_delete' = explicitDeleteMode ?? (preserveConnection ? 'stop_syncing' : 'full_delete');

    const deletePayload = {
      user_id: userId,
      institution_id: connectionData.institutionId,
      provider_name: connectionData.providerName, // canonical param name
      institution_identifier: connectionData.institutionIdentifier,
      preserve_connection: preserveConnection,    // snake_case for edge function contract
      delete_mode,
      operation_mode: 'local'                     // local-only DB cleanup; no external API
    } as const;

    logger.debug('persistence-utils', 'Delete payload structure', {
      payloadKeys: Object.keys(deletePayload),
      userIdPresent: !!userId,
      institutionIdPresent: !!connectionData.institutionId,
      providerName: connectionData.providerName,
      deleteMode: delete_mode,
      preserveConnection
    });

    await ensureValidToken('wealthica2-delete-accounts');
    const { data, error } = await supabase.functions.invoke('wealthica2-delete-accounts', {
      body: deletePayload
    });

    if (error) {
      logger.error('persistence-utils', 'Failed to delete accounts and positions', { 
        error: error.message || error,
        statusCode: error.status || 'unknown',
        payloadKeys: Object.keys(deletePayload)
      });
      return { success: false, error, details: { 
        statusCode: error.status,
        payloadStructure: Object.keys(deletePayload)
      }};
    } else {
      logger.info('persistence-utils', 'Successfully deleted accounts and positions', { data });
      return { success: true, details: data };
    }
  } catch (err) {
    logger.error('persistence-utils', 'Error deleting accounts and positions', { error: String(err) });
    return { success: false, error: err };
  }
}

/**
 * Delete accounts from the database
 * 
 * @deprecated Use useWealthicaDisconnect hook instead for better error handling and React Query integration
 * @param userId - User ID
 * @param institutionId - Institution ID
 */
export async function deleteAccountsFromDatabase(
  userId: string, 
  institutionId: string,
  preserveConnection = false
): Promise<{success: boolean, error?: any}> {
  try {
    logger.warn('persistence-utils', 'deleteAccountsFromDatabase is deprecated. Use useWealthicaDisconnect hook instead.');
    logger.info('persistence-utils', 'Deleting accounts (legacy method)', { institutionId });

    const deletePayload = {
      user_id: userId,
      institution_id: institutionId,
      preserveConnection
    };

    await ensureValidToken('wealthica2-delete-accounts');
    const { error } = await supabase.functions.invoke('wealthica2-delete-accounts', {
      body: deletePayload
    });

    if (error) {
      logger.error('persistence-utils', 'Failed to delete accounts', { error: error.message });
      return { success: false, error };
    } else {
      logger.info('persistence-utils', 'Successfully deleted accounts');
      return { success: true };
    }
  } catch (err) {
    logger.error('persistence-utils', 'Error deleting accounts', { error: String(err) });
    return { success: false, error: err };
  }
}
